import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  Stack,
} from "@fluentui/react";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  SETChileComponentName,
  SETHasServiceRequestTypeLogData,
  SETIsCustomeLog,
  SETLogEventData,
  SETServiceRequestTypeLogData,
} from "../../../../../core/store";
import { postConsumptionAPI } from "../../../../utils/httpUtils";
import { ServiceContext } from "@msx/platform-services";
import { saveUserTileProfileApi, UserTileProfilePayload, fetchSubSegments, getJsonKeyForDialogTitle, createRequestTypeProfilePayload, isSubSegmentHiddenForTile } from "../../../../utils/segmentApiUtils";

const DynamicDropdownGeneric = ({
  numberOfDropdown = 0,
  firstDropDownOptions = [],
  dialogTitle = "",
  dialogSubText = "",
  isVisible = false,
  modalValue = "",
  openModal = (a, b) => { },
  openWebForm = (e) => { },
  onHide = () => { },
  onCancel = () => { },
  onHideAfterSave = null as (() => void) | null,
  secondDropDownOptions = [],
  passSegmentDataToIris = (segment, subSegment, redirectServiceIdForSMEC = 0) => { },
  isDependent = true,
  getAllSubSegmentFromSegment = (a) => { },
  setEnterprise,
  card,
  isTileHasForm,
  dismissPanel,
  setOpenPanelId,
  openPanelForSMEC,
  dataServiceWorkspace,
  isopenSegment,
  userSelectedOption = "",
  isRedirectFromAnotherForm,
  tileProfileResponse,
  isAllowRendering,
  isDetailClick,
  openWindowFrame = (url: string, openinBrowserTab: boolean) => { },
}) => {
  // Destructure card properties
  const { serviceID, isRedirect, redirectServiceIdForSMEC, serviceRequestFormLink, openinBrowserTab, isSegment, service_IsNonIRISService, isNonIRISService } = card || {};
  // The non-IRIS flag has two field names depending on the source API:
  //  - workspace tiles (GetUserWorkSpace)  -> service_IsNonIRISService
  //  - catalog tiles   (GetAllServices)    -> isNonIRISService
  // Read whichever is present so the IFrame gate works regardless of the tile's data source.
  const nonIrisServiceFlag = service_IsNonIRISService ?? isNonIRISService;

  // Tile-based Sub-Segment hide: for configured tiles (EnvironmentVariables "HIDE_SUBSEGMENT_TILES")
  // the Sub-Segment dropdown is never shown, regardless of what the Sub-Segment API returns.
  const hideSubSegmentForTile = isSubSegmentHiddenForTile(dialogTitle);
  // Show Sub-Segment only when the tile is not force-hidden AND the API actually returned options.
  const showSubSegment = !hideSubSegmentForTile && secondDropDownOptions.length > 0;

  const context = useContext(ServiceContext);
  const [firstSelectedOption, setFirstSelectedOption] = useState(null);
  const [secondSelectedOption, setSecondSelectedOption] = useState("");
  const [firstSelectedText, setFirstSelectedText] = useState(null);
  const [secondSelectedText, setSecondSelectedText] = useState("");
  const [value, setValue] = useState(modalValue);
  const [openInNewTab, setOpenInNewTab] = useState(false);
  const reduxDispatch = useDispatch();
  const [firstSelectedOptionAm, setFirstSelectedOptionAm] = useState<string>();
  const [isProcessingTileProfile, setIsProcessingTileProfile] = useState(false);

  /**
   * Flag to prevent dialog from showing after automated tile profile processing completes.
   * 
   * RACE CONDITION PROBLEM:
   * When user clicks Start button in InfoPanel, the following execution flow occurs:
   * 
   * 1. InfoPanel onClick → checkIrisService (V2_Tiles line 1368)
   * 2. checkIrisService → ifandelsecondition → IsNonIrisService → handleFormAndSegment
   * 3. handleFormAndSegment calls setSegmentDropdownVisible(true) 
   * 4. Simultaneously, handleTileProfileResponse executes in V2_DynamicDropdown
   * 
   * EXECUTION TIMELINE CAUSING ISSUE:
   * - Initial state: isVisible = false (setSegmentDropdownVisible default)
   * - handleTileProfileResponse starts processing
   * - onHide() called at line 141 → isVisible changes to false
   * - Processing ends, but then setSegmentDropdownVisible(true) executes
   * - isVisible changes to true → popup displays (unwanted behavior)
   * 
   * SOLUTION:
   * This flag ensures that once automated tile profile processing completes successfully,
   * the dialog remains permanently hidden regardless of subsequent setSegmentDropdownVisible(true) calls.
   * The dialog's hidden property becomes: hidden={!isVisible || isProcessingTileProfile || hasProcessedTileProfile}
   */
  const [hasProcessedTileProfile, setHasProcessedTileProfile] = useState(false);

  // Function to reset internal component state
  const resetInternalState = () => {
    setFirstSelectedOption(null);
    setSecondSelectedOption("");
    setFirstSelectedText(null);
    setSecondSelectedText("");
    setFirstSelectedOptionAm(undefined);
    setHasProcessedTileProfile(false);
  };

  // Set the pre-selected segment when user is redirected from another form
  useEffect(() => {
    // Check if we need to set pre-selected values (for both redirect scenarios)
    if (userSelectedOption && firstDropDownOptions.length > 0) {
      // Find the segment key based on the selected segment text
      const selectedSegment = firstDropDownOptions.find(option =>
        option.text.toLowerCase() === userSelectedOption.toLowerCase()
      );
      if (selectedSegment) {
        // Set for Americas tile dropdown (used in redirect scenarios)
        setFirstSelectedOptionAm(selectedSegment.key.toString());
        // Also set for regular dropdown (for consistency)
        setFirstSelectedOption(selectedSegment.key);
        setFirstSelectedText(selectedSegment.text);

        // Load subsegments for the pre-selected segment
        getAllSubSegmentFromSegment(selectedSegment.key);
      }
    }
  }, [userSelectedOption, firstDropDownOptions, isRedirect, isRedirectFromAnotherForm]);


  useEffect(() => {
    if (isAllowRendering === false || isDetailClick === true) return;
    // For P-URL segment tiles with NO saved profile, block auto-process so user sees the dialog.
    // If saved profile exists (profileJson not null), let auto-process run — ghost ticket
    // prevention is handled in passSegmentDataToIris (skips handleChatBotIconClick for redirect tiles).
    if (sessionStorage.getItem("PUrlSegmentPending") === "true" && !tileProfileResponse?.profileJson) return;
    // Validate that segments value exists in firstDropDownOptions

    if (numberOfDropdown === 1) {
      handleTileProfileResponseforOneDropDownOptions();
      return;
    }

    const segmentOption = firstDropDownOptions.find(option => option.key === tileProfileResponse.profileJson?.segments);
    if (!segmentOption) {
      return;
    }

    if (isRedirect && segmentOption.text === "SME&C") {
      handleSMECTileProfileProcessing();
      return;
    }

    if (!firstDropDownOptions?.length || isAllowRendering === false || isRedirectFromAnotherForm === true) return;
    const isAmericasTile = isRedirect && !isRedirectFromAnotherForm; // isAmericasTile means tile contains some redirection from one tile to another tile i.e isRedirect = true and redirectServiceIdForSMEC is not null.
    const isSegmentSubsegmentTile = numberOfDropdown === 2;
    if (isAmericasTile) {
      handleTileProfileResponseAmericas();
    } else if (isSegmentSubsegmentTile) {
      handleTileProfileResponse();
    }
  }, [tileProfileResponse, firstDropDownOptions, numberOfDropdown, isRedirectFromAnotherForm, isAllowRendering]);

  const handleTileProfileResponseAmericas = async () => {
    if (!tileProfileResponse?.isSuccess || !tileProfileResponse?.profileJson?.segments) {
      return;
    }

    // Extract values to variables for better readability
    const segments = tileProfileResponse.profileJson.segments;
    const subSegments = tileProfileResponse.profileJson.subSegments;

    setIsProcessingTileProfile(true); // Hide dialog during processing

    try {
      // Validate that segments value exists in firstDropDownOptions
      const segmentOption = firstDropDownOptions.find(option => option.key === segments);
      if (!segmentOption) {
        return;
      }

      // Fetch sub-segments using the segments value
      const subSegmentsData = await fetchSubSegments(context, segments);

      // Validate that subSegments value exists in subSegmentsData (only if subSegments is provided)
      if (subSegments) {
        const subSegmentOption = subSegmentsData.find(subSeg => subSeg.id === subSegments);
        if (!subSegmentOption) {
          return;
        }
      }

      // Convert subSegmentsData to dropdown format for consistency
      const subSegmentDropDownOptions = subSegmentsData.map(subSeg => ({ key: subSeg.id, text: subSeg.name }));

      await handleSaveAmericas(firstDropDownOptions, subSegmentDropDownOptions, segments.toString(), subSegments?.toString() || "", false);
      // Hide the dialog after successful execution
      onHide();
      // Set flag to prevent popup from showing again
      setHasProcessedTileProfile(true);
    } catch (error) {
      console.error("Error processing Americas tile profile response:", error);
    } finally {
      setIsProcessingTileProfile(false);
    }
  };


  const handleTileProfileResponseforOneDropDownOptions = async () => {
    // Step 1: Check if numberOfDropdown === 1 and firstDropDownOptions has items
    if (numberOfDropdown !== 1 || !firstDropDownOptions?.length) {
      return;
    }

    // Check if tileProfileResponse.isSuccess === true
    if (!tileProfileResponse?.isSuccess) {
      return;
    }

    // Get the JSON key based on dialogTitle
    const jsonKey = getJsonKeyForDialogTitle(dialogTitle);

    // If no key found, exit early
    if (!jsonKey) {
      return;
    }

    // Step 2: Check if jsonKey is present in tileProfileResponse.profileJson
    if (!tileProfileResponse.profileJson || !(jsonKey in tileProfileResponse.profileJson)) {
      return;
    }

    // Get the value from profileJson using the jsonKey
    const profileValue = tileProfileResponse.profileJson[jsonKey];

    // Step 3: Extract last character of key in each item and find matching option
    const matchingOption = firstDropDownOptions.find(option => {
      // Extract last character from key
      const lastChar = option.key.slice(-1);
      // Convert to number
      const numericValue = Number(lastChar);

      // Check if extraction is valid and matches profileValue
      return !isNaN(numericValue) && numericValue === profileValue;
    });

    if (!matchingOption) {
      return;
    }

    setIsProcessingTileProfile(true); // Hide dialog during processing

    try {
      // Process the matched option
      await onGetStarted(matchingOption.value, matchingOption, matchingOption.openinBrowserTab);
      // Hide the dialog after successful execution
      //onHide();
      // Set flag to prevent popup from showing again
      setHasProcessedTileProfile(true);
    } catch (error) {
      console.error("Error processing one dropdown tile profile:", error);
    } finally {
      setIsProcessingTileProfile(false);
    }
  };

  const handleTileProfileResponse = async () => {
    // Early return only if there is no saved segment. Sub-segment is optional: segment-only
    // tiles (Sub-Segment hidden via EnvironmentVariables "HIDE_SUBSEGMENT_TILES", or new segment
    // model like "MS Elevate"/"SME&C - Corporate") persist subSegments = 0. Requiring subSegments
    // here made the saved profile never auto-process, so the popup reappeared on every Start click.
    if (!tileProfileResponse?.isSuccess || !tileProfileResponse?.profileJson?.segments) {
      return;
    }
    // Extract values to variables for better readability
    const segments = tileProfileResponse.profileJson.segments;
    const subSegments = tileProfileResponse.profileJson.subSegments;

    setIsProcessingTileProfile(true); // Hide dialog during processing

    try {
      // Validate that segments value exists in firstDropDownOptions
      const segmentOption = firstDropDownOptions.find(option => option.key === segments);
      if (!segmentOption) {
        return;
      }

      // Resolve sub-segment name only when the saved profile carries one.
      let subSegmentName = "N/A";
      if (subSegments) {
        // Fetch sub-segments using the segments value
        const subSegmentsData = await fetchSubSegments(context, segments);
        // Validate that subSegments value exists in subSegmentsData
        const subSegmentOption = subSegmentsData.find(subSeg => subSeg.id === subSegments);
        if (!subSegmentOption) {
          return;
        }
        subSegmentName = subSegmentOption.name;
      }

      await handleSegmentSubSegmentSave(segmentOption.text, subSegmentName, dialogTitle, segments, subSegments, false);
      // Hide the dialog after successful execution
      onHide();
      // Set flag to prevent popup from showing again
      setHasProcessedTileProfile(true);
    } catch (error) {
      console.error("Error fetching sub-segments:", error);
    } finally {
      setIsProcessingTileProfile(false);
    }
  };

  const handleChangeFirstDropdownAmericas = (
    event: any,
    option?: IDropdownOption
  ) => {
    const newKey = option ? option.key.toString() : undefined;
    setFirstSelectedOptionAm(newKey);
    setSecondSelectedOption("");
    setSecondSelectedText("");
    // Also load subsegments when user manually selects an option
    if (option) {
      setFirstSelectedOption(option.key);
      setFirstSelectedText(option.text);
      getAllSubSegmentFromSegment(option.key);
    }
  };


  const handleChangeFirstDropdown = (event, option) => {
    if (numberOfDropdown === 1) {
      setFirstSelectedOption(option);
      setValue(option.value);
      setOpenInNewTab(option.openinBrowserTab);
      isVisible = false;
    } else {
      setFirstSelectedOption(option.key);
      setFirstSelectedText(option.text);
      getAllSubSegmentFromSegment(option.key);
    }
  };

  const handleSecondDropdownChange = (event, option) => {
    setSecondSelectedOption(option.key);
    setSecondSelectedText(option.text);
  };

  // Helper function for request type dropdown event logging
  const logRequestTypeStartEvent = (serviceName: string, requestType: string) => {
    reduxDispatch(SETChileComponentName("Dialog " + serviceName));
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "Request Type dropdown Start",
        elementId: "",
        action: "click",
        message: `User click on Request Type dropdown Start`,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(
      SETServiceRequestTypeLogData({
        ServiceName: serviceName,
        RequestType: requestType || "",
        TileName: "",
        IsNonIrisService: "true",
        IrisUtterance: "",
        profileSetting: `v2`,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
  };

  const onGetStarted = async (urlValue: string, selectedOption: any, openInNewTabFlag: boolean) => {
    // Parse the URL
    const urlParams = new URLSearchParams(urlValue);

    // Check if the URL contains the requestType parameter
    if (urlParams.has("requestType")) {
      // Get the value of the requestType parameter
      const requestType = urlParams.get("requestType");

      const formattedRequestType = requestType.replace(/\s/g, "").toLowerCase();
      const formattedSelectedOption = selectedOption.text
        .replace(/\s/g, "")
        .toLowerCase();
      // Check if the requestType meets your criteria
      if (
        selectedOption &&
        formattedRequestType === formattedSelectedOption
      ) {
        // Open the webform or perform any action
        setFirstSelectedOptionAm(null);
        openWebForm(formattedRequestType);
        onCancel();
      }
    } else {
      setFirstSelectedOptionAm(null);
      openModal(urlValue, openInNewTabFlag);
      onCancel();
    }
    // Log event
    logRequestTypeStartEvent(dialogTitle, selectedOption.text);
  };

  // Helper function for segment/sub-segment save event logging
  const logSegmentSubSegmentSaveEvent = (segmentText: string, subSegmentText: string, serviceName: string) => {
    // Start Event Logging //
    reduxDispatch(SETChileComponentName("v2SegSubSegDropdown"));
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "Save Button Click v2SegSubSegDropdown Seg",
        elementId: "",
        action: "click",
        message: `Save Button Click v2SegSubSegDropdown`,
        UserSettingsSegment: segmentText,
        UserSettingsSubSegment: subSegmentText,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(
      SETServiceRequestTypeLogData({
        ServiceName: serviceName,
        TileName: "",
        IsNonIrisService: "",
        IrisUtterance: "",
        UserSettingsSegment: segmentText,
        UserSettingsSubSegment: subSegmentText,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    // End Event Logging //
  };

  // Helper function to save tile profile
  const saveTileProfile = async (selectedSegment: any, selectedSubSegment: any, numberOfDropdown: number) => {
    if (tileProfileResponse.isSuccess && tileProfileResponse.profileJson === null) {
      // Create JSON based on numberOfDropdown
      const Json = numberOfDropdown === 1
        ? { segments: selectedSegment }
        : { segments: selectedSegment, subSegments: selectedSubSegment };

      // Create ProfileJson as a JSON string
      const profileJson = JSON.stringify(Json);

      const payload: UserTileProfilePayload = {
        ID: serviceID,
        ProfileJson: profileJson
      };

      await saveUserTileProfileApi(context, payload);
    }
  };

  // Helper function to handle segment/sub-segment save operations
  const handleSegmentSubSegmentSave = async (segmentText: string, subSegmentText: string, serviceName: string, selectedSegment: any, selectedSubSegment: any, shouldSaveTileProfile: boolean = false) => {
    logSegmentSubSegmentSaveEvent(segmentText, subSegmentText, serviceName);
    passSegmentDataToIris(segmentText, subSegmentText, redirectServiceIdForSMEC);

    if (shouldSaveTileProfile) {
      await saveTileProfile(selectedSegment, selectedSubSegment, 2);
    }

    onCancel();
  };

  // Helper function to save request type tile profile (for tiles with numberOfDropdown = 1)
  const saveRequestTypeTileProfile = async () => {
    if (tileProfileResponse.isSuccess && tileProfileResponse.profileJson === null) {
      const payload = createRequestTypeProfilePayload(serviceID, firstSelectedOption, dialogTitle);

      if (payload) {
        await saveUserTileProfileApi(context, payload);
      }
    }
  };

  // Function to handle save action for the tile having number of dropdowns = 1 EG: "Business Insights Requests" tile.
  const handleRequestTypeSave = async (shouldSaveTileProfile: boolean = false) => {
    onGetStarted(value, firstSelectedOption, openInNewTab);

    if (shouldSaveTileProfile) {
      await saveRequestTypeTileProfile();
    }
  };

  const handleSave = () => {
    // Clear P-URL auto-process block — user explicitly saving segment (legitimate interaction)
    sessionStorage.removeItem("PUrlSegmentPending");
    if (numberOfDropdown === 1) {
      handleRequestTypeSave(true);
    } else if (numberOfDropdown === 2) {
      handleSegmentSubSegmentSave(firstSelectedText, secondSelectedText, dialogTitle, firstSelectedOption, secondSelectedOption, true);
    }
  };

  const handleDropdowns = () => {
    switch (numberOfDropdown) {
      case 1: {
        return (
          <Dropdown
            placeholder="Select an option"
            options={firstDropDownOptions}
            selectedKey={
              firstSelectedOption ? firstSelectedOption.key : undefined
            }
            onChange={handleChangeFirstDropdown}
            className="customDropdown"
            aria-label={dialogTitle + " " + dialogSubText}
            calloutProps={{
              styles: {
                root: {
                  zIndex: 100000001, // 👈 Higher than dialog
                },
              },
              layerProps: {
                styles: {
                  root: { zIndex: 100000001 }, // Ensure layer has higher zIndex too
                },
              },
            }}
            styles={{
              root: {
                width: 300,
                marginTop: -20,
                marginBottom: 10,
              },
            }}
          />

        );
      }
      case 2: {
        return (
          <>
            {
              isopenSegment &&
              <Dropdown
                label="Segment"
                selectedKey={firstSelectedOption}
                onChange={handleChangeFirstDropdown}
                options={firstDropDownOptions}
                required
                placeholder="Please select a Segment"
                calloutProps={{
                  styles: {
                    root: {
                      zIndex: 100000001, // 👈 Higher than dialog
                    },
                  },
                  layerProps: {
                    styles: {
                      root: { zIndex: 100000001 }, // Ensure layer has higher zIndex too
                    },
                  },
                }}
                styles={{
                  root: {
                    width: 300,
                    marginTop: -20,
                    marginBottom: 10,
                  },
                }}
              />
            }
            {showSubSegment && (!isDependent || firstSelectedOption || isRedirectFromAnotherForm) && (
              <Dropdown
                label="Subsegment"
                selectedKey={secondSelectedOption}
                onChange={handleSecondDropdownChange}
                options={secondDropDownOptions}
                required
                placeholder="Please select a Subsegment"
                calloutProps={{
                  styles: {
                    root: {
                      zIndex: 100000001, // 👈 Higher than dialog
                    },
                  },
                  layerProps: {
                    styles: {
                      root: { zIndex: 100000001 }, // Ensure layer has higher zIndex too
                    },
                  },
                }}
                styles={{
                  root: {
                    width: 300,
                    marginBottom: 10,
                  },
                }}
              />
            )}
          </>
        );
      }
      default: {
        return <></>;
      }
    }
  };

  const handleDropdownsAmericasTile = () => {
    // Convert selectedKey to match option key type for proper matching
    let normalizedSelectedKey;
    if (firstSelectedOptionAm) {
      normalizedSelectedKey = isNaN(Number(firstSelectedOptionAm)) ? firstSelectedOptionAm : Number(firstSelectedOptionAm);
    } else {
      normalizedSelectedKey = undefined;
    }

    // Convert secondSelectedOption to match option key type for proper matching
    // Copilot: Ensure empty string is treated as undefined to prevent auto-selection when segment changes
    let normalizedSecondSelectedKey;
    if (secondSelectedOption && secondSelectedOption !== "") {
      normalizedSecondSelectedKey = isNaN(Number(secondSelectedOption)) ? secondSelectedOption : Number(secondSelectedOption);
    } else {
      normalizedSecondSelectedKey = undefined;
    }

    return (
      <>
        <Dropdown
          label="Segment"
          selectedKey={normalizedSelectedKey}
          onChange={handleChangeFirstDropdownAmericas}
          options={firstDropDownOptions}
          required
          placeholder="Please select a Segment"
          calloutProps={{
            styles: {
              root: {
                zIndex: 100000001, // 👈 Higher than dialog
              },
            },
            layerProps: {
              styles: {
                root: { zIndex: 100000001 }, // Ensure layer has higher zIndex too
              },
            },
          }}
          styles={{
            root: {
              width: 300,
              marginTop: -20,
              marginBottom: 10,
            },
          }}
        />
        {firstSelectedOptionAm && showSubSegment && (
          <Dropdown
            key={`sub-segment-${firstSelectedOptionAm}-${secondDropDownOptions.length}`}
            label="Sub-Segment"
            selectedKey={normalizedSecondSelectedKey === undefined ? null : normalizedSecondSelectedKey}
            onChange={handleSecondDropdownChange}
            options={secondDropDownOptions}
            required
            placeholder="Please select a Sub-Segment"
            calloutProps={{
              styles: {
                root: {
                  zIndex: 100000001, // 👈 Higher than dialog
                },
              },
              layerProps: {
                styles: {
                  root: { zIndex: 100000001 }, // Ensure layer has higher zIndex too
                },
              },
            }}
            styles={{
              root: {
                width: 300,
                marginBottom: 10,
              },
            }}
          />
        )}
      </>
    );
  };

  const handleSaveAmericas = async (segmentDropDownOptions: any[], subSegmentDropDownOptions: any[], segmentKey: string, subsegmentkey: string, shouldSaveTileProfile: boolean) => {
    // Segment options must be available. Sub-segment is optional: the new segment
    // model (e.g. "SME&C - Corporate", "SME&C - SMB", "MS Elevate") encodes the
    // sub-segment in the segment value itself, so those tiles return no sub-segments.
    if (!segmentDropDownOptions?.length) {
      console.error("No segment options available");
      return;
    }

    // IFrame/segment tiles (e.g. AM Tool, Account Management Request (Americas)) launch the external
    // tool via serviceRequestFormLink on a SEGMENT-only selection and never consume a sub-segment,
    // so treat the sub-segment as absent even if the Sub-Segment API returned options for the segment.
    const isIframeSegmentTile = !!nonIrisServiceFlag && !!isSegment &&
      serviceRequestFormLink != null &&
      !(serviceRequestFormLink?.startsWith("[") && serviceRequestFormLink?.endsWith("]"));

    // Sub-segment is treated as absent when it is force-hidden for this tile
    // (EnvironmentVariables "HIDE_SUBSEGMENT_TILES"), the tile is an IFrame/segment tile,
    // or the segment returned no options.
    const hasSubSegments = !hideSubSegmentForTile && !isIframeSegmentTile && (subSegmentDropDownOptions?.length ?? 0) > 0;

    // Find selected options and extract text in one step
    const selectedSegment = segmentDropDownOptions.find(option => option.key.toString() === segmentKey);
    const selectedSubSegment = hasSubSegments
      ? subSegmentDropDownOptions.find(option => option.key.toString() === subsegmentkey)
      : undefined;

    // Segment is always required; sub-segment is required only when the segment has sub-segments
    if (!selectedSegment || (hasSubSegments && !selectedSubSegment)) {
      const missingType = selectedSegment ? 'subsegment' : 'segment';
      const missingValue = selectedSegment ? subsegmentkey : segmentKey;
      console.error(`Selected ${missingType} '${missingValue}' not found in available options`);
      return;
    }

    // Normalize segment text for case-insensitive comparison.
    const segmentText = (selectedSegment.text || "").toLowerCase();

    // Set common session storage values. Persist "N/A" when the segment has no sub-segment.
    sessionStorage.setItem("Segment", selectedSegment.text);
    sessionStorage.setItem("SubSegment", selectedSubSegment?.text || "N/A");

    // Handle segment-specific logic with case-insensitive matching.
    // AM Tool / Account Management Request (Americas) tiles return only a segment
    // (no sub-segment), so route on the segment value alone.
    //  - Enterprise / MS Elevate -> external IFrame (openWindowFrame) when it's an IFrame tile,
    //                               otherwise the enterprise form.
    //  - SME&C                   -> workspace redirect.
    if (segmentText === "enterprise" || segmentText === "ms elevate") {
      // NOTE: the non-IRIS flag (nonIrisServiceFlag) and isSegment come back from the API as
      // 1 / true / "true" depending on the source, so compare truthily — a strict `=== true`
      // here made the gate fail and the IFrame never opened for Enterprise / MS Elevate.
      const hasScalarLink = serviceRequestFormLink != null && !(serviceRequestFormLink?.startsWith("[") && serviceRequestFormLink?.endsWith("]"));
      const isIFrameRequest = hasScalarLink && !!nonIrisServiceFlag && !!isSegment;
      if (isIFrameRequest) {
        openWindowFrame(serviceRequestFormLink, openinBrowserTab);
      } else {
        setEnterprise(true);
      }
    } else if (segmentText === "sme&c" || segmentText === "smec" || segmentText === "sme & c") {
      await handleAddToWorkspace(selectedSegment.text, selectedSubSegment?.text || "N/A", segmentKey, subsegmentkey, shouldSaveTileProfile);
    }
    else {
    }

    if (shouldSaveTileProfile) {
      // Save tile profile using helper function. Sub-segment persisted as 0 when absent.
      await saveTileProfile(selectedSegment?.key, selectedSubSegment?.key ?? 0, 2); // numberOfDropdown = 2 for Americas tiles (segment and subsegment)
    }

    setFirstSelectedOptionAm(null);
    // Americas tiles handle their own form logic (Enterprise=iframe, SME&C=redirect, Corp=nothing).
    // Use onHide (clears pendingState) to prevent the pendingState useEffect from firing
    // openWebFormFinal → handleChatBotIconClick → ghost ticket.
    onHide();
  };

  const onCancelAmericas = () => {
    resetInternalState(); // Reset state before closing
    setEnterprise(false);
    onHide();
  };

  // handler function
  const handleAddToWorkspace = async (segmentText: string, subSegmentText: string, segmentKey: any, subsegmentkey: any, shouldSaveTileProfile: boolean) => {
    try {

      sessionStorage.setItem("firstSelectedOptionAm", segmentText);
      const alreadyExists = dataServiceWorkspace.some(
        (workspace) => workspace.serviceID === redirectServiceIdForSMEC
      );

      if (alreadyExists) {
        openPanelForSMEC(redirectServiceIdForSMEC, segmentKey, subsegmentkey, false, true, false, shouldSaveTileProfile);
        return;
      }
      // Step 3: Call POST API if it doesn't exist
      const apiUrl = "/api/V2_UserWorkSpace/Add";
      const postData = {
        serviceId: redirectServiceIdForSMEC,
      };
      const postResponse = await postConsumptionAPI(apiUrl, postData, context.authClient);

      if (postResponse.status >= 200 && postResponse.status < 300) {
        resetInternalState(); // Reset state before redirecting
        openPanelForSMEC(redirectServiceIdForSMEC, segmentKey, subsegmentkey, false, true, true, shouldSaveTileProfile);
        dismissPanel();
      }
    } catch (error) {
      console.error("An error occurred while processing AddToWorkspace.", error);
    }
  };

  const handleSMECTileProfileProcessing = async () => {
    // Early return if tile profile response is not valid
    if (!tileProfileResponse?.isSuccess || !tileProfileResponse?.profileJson?.segments) {
      return;
    }

    // Validate that segments value exists in firstDropDownOptions
    const segmentOption = firstDropDownOptions.find(option => option.key === tileProfileResponse.profileJson?.segments);
    if (!segmentOption) {
      return;
    }

    if (isRedirect && segmentOption.text === "SME&C") {
      // Extract values to variables for better readability
      const segments = tileProfileResponse.profileJson.segments;
      const subSegments = tileProfileResponse.profileJson.subSegments;

      setIsProcessingTileProfile(true); // Hide dialog during processing

      try {
        // Fetch sub-segments using the segments value
        const subSegmentsData = await fetchSubSegments(context, segments);

        // Validate that subSegments value exists in subSegmentsData (only if subSegments is provided)
        if (subSegments) {
          const subSegmentOption = subSegmentsData.find(subSeg => subSeg.id === subSegments);
          if (!subSegmentOption) {
            return;
          }
          // Process SME&C tile with segment and subsegment data
          await handleAddToWorkspace(segmentOption.text, subSegmentOption.name, segments, subSegments, false);
          await handleSegmentSubSegmentSave(segmentOption.text, subSegmentOption.name, dialogTitle, segments, subSegments, false);
        }
      } catch (error) {
        console.error("Error processing SME&C tile profile:", error);
      } finally {
        setIsProcessingTileProfile(false);
        // Hide the dialog after processing
        onHide();
        // Set flag to prevent popup from showing again
        setHasProcessedTileProfile(true);
      }
    }
  };


  const checkIsFieldsrequired = () => {
    // Sub-segment is only required when it is actually shown: not force-hidden for this tile
    // (EnvironmentVariables "HIDE_SUBSEGMENT_TILES") and the segment returned sub-segment options.
    const subSegmentRequired = showSubSegment;
    return (
      (numberOfDropdown === 1 && !firstSelectedOption) ||
      (numberOfDropdown === 2 &&
        !isRedirectFromAnotherForm &&
        (!firstSelectedOption || (subSegmentRequired && !secondSelectedOption)))
    );
  };

  return (
    <>
      {isRedirect && !isRedirectFromAnotherForm ? (
        <Dialog
          hidden={!isVisible || isProcessingTileProfile || hasProcessedTileProfile}
          onDismiss={() => {
            onHide();
          }}
          dialogContentProps={{
            type: DialogType.normal,
            title: dialogTitle,
            subText: dialogSubText,
          }}
          modalProps={{
            isBlocking: false,
            styles: {
              main: { maxWidth: 450, maxHeight: "auto" },
              layer: { zIndex: 10000000 },
            },
          }}
          styles={() => ({ main: { boxShadow: "none" } })}
          className="service-tile-model-dynamic"
        >
          <DialogFooter>
            <>
              {handleDropdownsAmericasTile()}
              <Stack
                horizontal
                tokens={{ childrenGap: 10 }}
                styles={{ root: { marginTop: 10 } }}
              >
                <PrimaryButton
                  aria-label="save"
                  onClick={() => handleSaveAmericas(firstDropDownOptions, secondDropDownOptions, firstSelectedOptionAm.toString(), secondSelectedOption.toString(), true)}
                  text={"Start"}
                  styles={{ root: { width: 100 } }}
                  disabled={!firstSelectedOptionAm || (showSubSegment && !secondSelectedOption)}
                />
                <DefaultButton
                  onClick={onCancelAmericas}
                  aria-label="cancel"
                  text="Cancel"
                  styles={{ root: { width: 100 } }}
                />
              </Stack>
            </>
          </DialogFooter>
        </Dialog>
      ) : (
        <Dialog
          hidden={!isVisible || isProcessingTileProfile || hasProcessedTileProfile}
          onDismiss={() => {
            onHide();
          }}
          dialogContentProps={{
            type: DialogType.normal,
            title: dialogTitle,
            subText: dialogSubText,
          }}
          modalProps={{
            isBlocking: false,
            styles: {
              main: { maxWidth: 450, maxHeight: "auto" },
              layer: { zIndex: 10000000 },
            },
          }}
          styles={() => ({ main: { boxShadow: "none" } })}
          className="service-tile-model-dynamic"
        >
          <DialogFooter>
            <>
              {handleDropdowns()}
              <Stack
                horizontal
                tokens={{ childrenGap: 10 }}
                styles={{ root: { marginTop: 10 } }}
              >
                <PrimaryButton
                  aria-label="save"
                  onClick={handleSave}
                  text={numberOfDropdown === 1 ? "Start" : "Save"}
                  disabled={checkIsFieldsrequired()}
                  styles={{ root: { width: 100 } }}
                />
                <DefaultButton
                  onClick={onCancel}
                  aria-label="cancel"
                  text="Cancel"
                  styles={{ root: { width: 100 } }}
                />
              </Stack>
            </>
          </DialogFooter>
        </Dialog>
      )}
    </>
  );
};
export default DynamicDropdownGeneric;

// Generated by Copilot
// updated on: 2025-09-26 | 2 lines added | [Manual] | Added code to append segment and subsegment | Time Taken 0 min
// updated on: 2025-11-02 | 500 lines added/updated | [Auto Generated] | Implemented comprehensive dynamic dropdown system with tile profile processing, race condition prevention, automated segment handling, and enhanced dialog management for Americas and segment/subsegment tiles | Time Taken 15 min
// updated on: 2025-11-07 | 73 lines added/updated | [Auto Generated] | Renders dual dropdown UI specifically for Americas tiles with segment/subsegment selection Handles key normalization for proper dropdown selection matching and conditional subsegment rendering | Time Taken 18 min
// updated on: 2026-07-07 | 12 lines added/updated | [Auto Generated] | FYRO: config-driven tile-based Sub-Segment hide via EnvironmentVariables "HIDE_SUBSEGMENT_TILES" (showSubSegment/hideSubSegmentForTile) across render, validation, Start button and handleSaveAmericas | Time Taken 6 min
// updated on: 2026-07-07 | 15 lines added/updated | [Auto Generated] | FYRO: handleTileProfileResponse no longer requires subSegments (was bailing on subSegments=0) so segment-only saved profiles auto-process and the Start popup is skipped on repeat visits | Time Taken 5 min
// updated on: 2026-07-07 | 30 lines updated | [Auto Generated] | FYRO: reverted AM Tool isIframeTile experiment - restored original HEAD routing in handleSaveAmericas (Enterprise + n/a -> isIFrameRequest gate -> openWindowFrame else setEnterprise; SME&C -> workspace) and removed the isIframeTile fast-path from handleTileProfileResponse; kept HIDE_SUBSEGMENT (absent sub-segment now defaults to "n/a" so Enterprise IFrame still triggers) | Time Taken 6 min
// updated on: 2026-07-07 | 6 lines updated | [Auto Generated] | FYRO: AM Tool / Account Management Request (Americas) tiles have no sub-segment, so route handleSaveAmericas on segment alone - both "Enterprise" and "MS Elevate" now go through the isIFrameRequest gate (openWindowFrame / IFrame, not the web form); dropped the subSegmentText === "n/a" requirement | Time Taken 3 min
// updated on: 2026-07-07 | 10 lines updated | [Auto Generated] | FYRO: AM Tool IFrame fix - read nonIris flag from either service_IsNonIRISService (workspace API) or isNonIRISService (GetAllServices), use truthy checks (API returns 1/'true') in the isIFrameRequest gate, and treat IFrame/segment tiles (isIframeSegmentTile) as having no sub-segment so the sub-segment validation guard no longer bails and blocks the IFrame | Time Taken 8 min
